import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { AppState } from 'src/app/state';
import { Language } from 'src/app/models/language';
import { Section } from 'src/app/models/section';
import { Subsection } from 'src/app/models/subsection';
import { templatesTinymceInitConfig } from 'src/app/shared/components/tinymce-editor/tinymce-editor-config';
import { selectSelectedSubsection } from 'src/app/dashboard/state/subsections/subsections.selectors';
import { setSelectedLanguage } from 'src/app/dashboard/state/templates/templates.actions';
import {
  selectSelectedLanguage,
  selectSupportedLanguages,
} from 'src/app/dashboard/state/templates/templates.selectors';
import { selectSelectedSection } from 'src/app/dashboard/state/sections/sections.selectors';
import { updateSubsection } from 'src/app/dashboard/state/subsections/subsections.actions';
import { uploadReset } from 'src/app/dashboard/state/upload/upload.actions';

const PROFILE_DESCRIPTION_IDENTIFIER = 'section-graph-summary';
const PROFILE_NAME_IDENTIFIER = 'subsection-name';

@Component({
  selector: 'dashboard-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, OnDestroy, AfterViewInit, AfterContentChecked {
  @Input() iconUuid: string;
  @Output() resetSubsection = new EventEmitter();

  profileForm: FormGroup;

  selectedSection: Section;
  selectedSubsection: Subsection;

  supportedLanguages: Language[];
  selectedLanguage: Language;

  PROFILE_DESCRIPTION_IDENTIFIER = PROFILE_DESCRIPTION_IDENTIFIER;

  tinymceInitConfig = templatesTinymceInitConfig;

  isEditorInitialized = false;
  isEditingProfile = false;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.profileForm = this.formBuilder.group({
      profileTranslations: this.formBuilder.array([]),
      hasDetails: [false],
      detailsPage: [],
    });

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => {
        this.selectedLanguage = language;
        this.changeDetectorRef.detectChanges();
      })
    );
    this.subscriptions.add(
      this.store.select(selectSelectedSection).subscribe((section) => (this.selectedSection = section))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedSubsection).subscribe((subsection) => {
        this.selectedSubsection = subsection;
        this.isEditingProfile = !!this.selectedSubsection?.id;
        this.removeAllTranslationFields();
        if (this.supportedLanguages) {
          this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
        }
      })
    );
  }

  ngAfterViewInit() {
    this.isEditorInitialized = true;
  }

  ngAfterContentChecked() {
    this.changeDetectorRef.detectChanges();
  }

  get profileTranslations(): FormArray {
    return this.profileForm.get('profileTranslations') as FormArray;
  }

  get hasDetails(): AbstractControl {
    return this.profileForm.get('hasDetails') as AbstractControl;
  }

  get detailsPage(): AbstractControl {
    return this.profileForm.get('detailsPage');
  }

  removeAllTranslationFields(): void {
    this.profileTranslations.clear();
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedSubsection?.translations?.filter(
      (translation) => translation?.language === languageCode
    );

    const description = this.formBuilder.group({
      id: [filteredTranslations?.length && filteredTranslations[1] ? filteredTranslations[1]?.id : 0],
      language: [languageCode],
      identifier: [PROFILE_DESCRIPTION_IDENTIFIER],
      translation: [
        filteredTranslations?.length && filteredTranslations[1] ? filteredTranslations[1]?.translation : '',
        [Validators.maxLength(330)],
      ],
    });

    this.profileTranslations.push(description);
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  saveProfile() {
    this.profileForm.markAllAsTouched();

    if (this.profileForm.invalid) {
      return;
    }

    const profileNameTranslations = this.selectedSubsection?.translations?.filter(
      (translation) => translation.identifier === PROFILE_NAME_IDENTIFIER
    );
    const allTranslations = [...profileNameTranslations, ...this.profileForm.value.profileTranslations];
    const detailsPage = {
      id: this.isEditingProfile ? this.selectedSubsection?.detail?.id : null,
      mediaUrl: this.profileForm.value.detailsPage?.mediaUrl,
      translations: this.profileForm.value.detailsPage?.detailTranslations,
    };

    let profile = {
      ...this.profileForm.value,
      templateSectionId: this.selectedSection.id,
      icon: this.selectedSubsection.icon,
      translations: allTranslations,
      detail: this.hasDetails.value ? detailsPage : null,
      bars: [],
    };

    delete profile.detailsPage;
    delete profile.profileTranslations;

    if (this.isEditingProfile) {
      profile = {
        ...profile,
        id: this.selectedSubsection.id,
      };
      this.store.dispatch(updateSubsection({ subsection: profile }));
    }
    this.resetSubsection.emit(true);
    this.store.dispatch(uploadReset({ uuid: this.iconUuid }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
